import React, { useState, useEffect } from 'react';
import defaultImg from './img/defaultImg.jpg';
import SolveQuestions from '../SolveQuestions';
import FlipCardWrapper from '../FlipCardWrapper';
import ConceptToCharacteristic from '../ConceptToCharacteristic';
import ConceptMatchingGame from '../ConceptMatchingGame';
import FlashcardQuiz from '../flashcard-quiz';
import { useClient } from 'jsonapi-react';
import MindMap from '@/app/components/MindMap';
import { useNavigate } from 'react-router-dom';
import { Loader } from '@/components/custom/loader';
import { GuideBlockArticle, GuideBlockArticleContent, GuideBlockArticleFooter, GuideBlockArticleHat, GuideBlockArticleHeader, GuideBlockArticleTitle } from '@/components/custom/guide';
import { Button } from '@/components/ui/button';

export function StudyGuideBlockArticle({ scorm, study_plan_video_id, introduction_image, last, allSectionsDone, allSections, setActiveSectionIndex, activeSectionIndex, setSections, setFinished }) {
  const navigate = useNavigate();
  const [goNext, setGoNext] = useState(false);
  const [scormQuestions, setScormQuestions] = useState([]);
  const client = useClient();
  const [loading, setLoading] = useState(false);

  const setScormSectionDone = async scormId => {
    const response = await client.mutate([`study_plan_videos/${study_plan_video_id}/scorm_contents/`, scormId], { done: true }, { method: 'put' });
    return response;
  };

  const nextSection = scornId => {
    setLoading(true);
    setScormSectionDone(scornId);

    setSections(prevSections => {
      return prevSections.map((section, index) => (index === activeSectionIndex ? { ...section, done: true } : section));
    });

    setActiveSectionIndex(activeSectionIndex + 1);
    setLoading(false);
  };

  const getScormQuestions = async study_plan_video_id => {
    let url = `study_plan_videos/${study_plan_video_id}/scorm_questions`;
    if (scorm.scorm.scorm_type === 'questions') {
      url = url.concat(`?type=questions`);
    } else if (scorm.scorm.scorm_type === 'learning_section') {
      url = url.concat(`?type=learning_section`);
    } else {
      url = '';
    }

    if (url !== '') {
      const response = await client.fetch(url);
      return response.data;
    }
  };

  const splitTopics = topics => {
    const conceptsData = topics.map(topic => ({
      id: topic.title.toLowerCase().replace(/\s+/g, ''),
      name: topic.title
    }));

    const meaningsData = topics.map(topic => ({
      id: topic.title.toLowerCase().replace(/\s+/g, ''),
      meaning: topic.content
    }));

    return { conceptsData, meaningsData };
  };

  useEffect(() => {
    getScormQuestions(study_plan_video_id).then(data => {
      setScormQuestions(data);
    });
  }, [scorm]);

  useEffect(() => {
    function handleScroll() {
      const screenHeight = document.documentElement.scrollHeight;
      const endPage = window.scrollY + window.innerHeight;

      if (endPage === screenHeight) setGoNext(false);
      else setGoNext(true);
    }

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [goNext]);

  const renderContent = data => {
    const scorm = data.scorm;
    switch (scorm.scorm_type) {
      case 'questions':
        return (
          <SolveQuestions
            data={scormQuestions}
            scorm={data}
            setScormSectionDone={setScormSectionDone}
            setFinished={setFinished}
          />
        );
      case 'educational_content':
        const { conceptsData, meaningsData } = splitTopics(scorm.content.main_topics);
        return (
          <>
            <GuideBlockArticleHeader>
              <GuideBlockArticleTitle>{scorm.content.main_title}</GuideBlockArticleTitle>
            </GuideBlockArticleHeader>

            {scorm.content.introduction.split('\n').map((paragraph, index) => (
              <p key={index}>{paragraph}</p>
            ))}

            <ConceptMatchingGame
              conceptsData={conceptsData}
              meaningsData={meaningsData}
            />
          </>
        );
      case 'introduction':
        return (
          <>
            <h2>{scorm.content.title}</h2>
            {introduction_image && (
              <div className="m-8 flex justify-center">
                <img
                  src={introduction_image || defaultImg}
                  alt="Imagem de introdução"
                />
              </div>
            )}

            {scorm.content.introduction.split('\n').map((paragraph, index) => (
              <p key={index}>{paragraph}</p>
            ))}
          </>
        );
      case 'mindmap':
        return (
          <>
            {scorm.content.introduction.split('\n').map((paragraph, index) => (
              <p key={index}>{paragraph}</p>
            ))}
            {scorm.content.main_nodes && <MindMap data={scorm.content.main_nodes} />}
            <br />
            <br />
            {scorm.content.conclusion.split('\n').map((paragraph, index) => (
              <p key={index}>{paragraph}</p>
            ))}
          </>
        );
      case 'learning_section':
        return (
          <>
            {scorm.content.introduction.split('\n').map((paragraph, index) => (
              <p key={index}>{paragraph}</p>
            ))}
            <br />
            <br />
            <ConceptToCharacteristic interactive_challenges={scorm.content.interactive_challenges} />
            <br />
            <br />
            {scorm.content.final_reflection.split('\n').map((paragraph, index) => (
              <p key={index}>{paragraph}</p>
            ))}
          </>
        );
      case 'learning_experience':
        return (
          <>
            <h2>{scorm.content.general_title}</h2>
            {scorm.content.introduction.split('\n').map((paragraph, index) => (
              <p key={index}>{paragraph}</p>
            ))}
            <FlipCardWrapper flip_cards={scorm.content.flip_cards} />
            <br />
            <br />
            {scorm.content.conclusion.split('\n').map((paragraph, index) => (
              <p key={index}>{paragraph}</p>
            ))}
          </>
        );
      case 'flashcard_quiz':
        return (
          <FlashcardQuiz
            onComplete={results => {
              setScormSectionDone(data.id);
              console.log('Quiz completed:', results);
            }}
            limit={scorm.content?.limit || 10}
          />
        );
      default:
        return <p>Tipo de conteúdo desconhecido.</p>;
    }
  };

  return (
    <GuideBlockArticle>
      {loading && <Loader />}

      {!loading && (
        <>
          <GuideBlockArticleHeader>
            {/* <GuideBlockArticleHat>Introdução</GuideBlockArticleHat> */}
            <GuideBlockArticleTitle>{scorm.scorm.content.title}</GuideBlockArticleTitle>
          </GuideBlockArticleHeader>

          <GuideBlockArticleContent>{renderContent(scorm)}</GuideBlockArticleContent>

          <GuideBlockArticleFooter>
            {!last && !allSectionsDone && (
              <Button
                variant="primary-light"
                border
                type="button"
                onClick={() => {
                  if (last) navigate('/guia-de-estudos');
                  nextSection(scorm.id);
                }}
              >
                Próxima seção
              </Button>
            )}
          </GuideBlockArticleFooter>
        </>
      )}
    </GuideBlockArticle>
  );
}
